/*global SunergoPlatform*/

/**
 * Initializes form change tracking.
 * @param {object} form - the form item.
 */
const initTrackChanges = function(form) {
	// Check if change tracking is enabled for this form.
	if (!form.element.dataset.trackChanges) return;

	const inputElements = SunergoPlatform.inputElements;

	/**
	 * Setup change log to track changes.
	 */
	form.changeLog = {
		items: [],

		// Find the action bar and status message locations.
		actionBar: form.element.querySelectorAll('[data-form-actions]'),
		statusMessage: document.querySelectorAll(`[data-track-changes-log="form_${form.name}"]`),

		// Load in i18n status labels for change log.
		changesLabel: {
			none: form.element.dataset.i18nChangesNone,
			single: form.element.dataset.i18nChangesSingle,
			multiple: form.element.dataset.i18nChangesMultiple
		},
		dialogDiscardChanges: false
	};

	// Setup listener function for browser native dialog when page is closed with changes.
	const beforeUnloadListener = function(event) {
		if (form.processing.status) return;

		event.preventDefault();
		return event.returnValue = 'Are you sure you want to exit?';
	};

	// Get and set dialog for Confirming Discard Changes.
	const dialog = SunergoPlatform.dialogs.get(`formDone_${form.name}`);
	if (dialog) {
		form.changeLog.dialogDiscardChanges = dialog;

		let timer = false;
		dialog.on('hide', () => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				if (form.changeLog.items && form.changeLog.items.length > 0) enableLeaveListener(true);
			}, 10);
		});
	}

	const linkClicked = function(event) {
		let targetHref = event.target;

		// If form processing, then disable leave listener.
		if (form.processing.status) return;

		// Check if target is a link or a child of a link.
		if (targetHref.tagName !== 'A') {
			if (!targetHref.closest('a')) return;
			else targetHref = targetHref.closest('a');
		}

		// Check if the target is not a page anchor.
		if (targetHref.getAttribute('href') && targetHref.getAttribute('href').indexOf('#') === 0) return;

		// Check if dialog is available.
		if (targetHref.target || !form.changeLog.dialogDiscardChanges) return;

		// Check if the target href is the button.
		const dialogButtonPrimary = form.changeLog.dialogDiscardChanges.$el.querySelector('[data-button-primary]');
		if (targetHref === dialogButtonPrimary) return;

		event.preventDefault();

		// Set hrefTarget to the dialog's primary button href.
		if (dialogButtonPrimary) dialogButtonPrimary.setAttribute('href', targetHref.href);

		// Show the dialog.
		form.changeLog.dialogDiscardChanges.show();
		enableLeaveListener(false);
	};

	let leaveListenerEnabled = false;
	const enableLeaveListener = function(enable) {
		if (enable && !leaveListenerEnabled) {
			window.addEventListener('beforeunload', beforeUnloadListener, false);
			document.addEventListener('click', linkClicked, false);
		} else if (leaveListenerEnabled) {
			window.removeEventListener('beforeunload', beforeUnloadListener, false);
			document.removeEventListener('click', linkClicked, false);
		}
		leaveListenerEnabled = enable;
	};

	form.changeLog.findItem = function(name) {
		for (let i = 0; i < this.items.length; i++) {
			const field = this.items[i];
			if (field.name == name) return true;
		}
		return false;
	};

	/**
	 * Updates the status message to show how many changes are made.
	 * @returns console error if there is no place to put status messages.
	 */
	let updatingStatusTimer = false;
	let output = '';
	form.changeLog.updateStatus = function() {
		let actionable = false;

		const statusMessage = this.statusMessage;
		const currLength = this.items.length;
		const hasChanges = currLength > 0;
		enableLeaveListener(hasChanges);

		// If there are any changes
		if (hasChanges) {
			// Build the changes status message.
			output = `<strong>${currLength}</strong> ${
				currLength == 1 ? this.changesLabel.single : this.changesLabel.multiple
			}`;
			actionable = true;
		} else {
			output = this.changesLabel.none;
		}

		// Show/hide reset button
		if (form.changeLog.resetButton) form.changeLog.resetButton.show(hasChanges);

		// Set the action bar's status.
		if (form.embedded || (statusMessage && statusMessage.length > 0))
			this.actionBar.forEach(function(item) {
				const contentBlock = item.closest('.content-block');
				if (form.embedded && contentBlock) contentBlock.dataset.edited = actionable;
				item.dataset.actionable = actionable;
			});

		if (!statusMessage) return;

		// Set status messages.
		if (updatingStatusTimer === false) clearTimeout(updatingStatusTimer);
		
		let isUpdating = true;
		statusMessage.forEach(function(item) {
			if (!isUpdating || item.innerHTML == output) isUpdating = false;
			else item.dataset.updating = true;
		});
		if (!isUpdating) return;
		updatingStatusTimer = setTimeout(function() {
			statusMessage.forEach(function(item) {
				item.innerHTML = output;
				item.removeAttribute('data-updating');
			});
		}, 50);
	};

	/**
	 * Add fields to change log.
	 * @param {HTMLElement} input - the input element.
	 */
	form.changeLog.add = function(input) {
		const index = this.items.indexOf(input);
		if (index > -1) return;
		
		// if input isn't found in items, then add and update counter.
		this.items.push(input);
		this.updateStatus();
	};

	/**
	 * Remove fields from change log.
	 * @param {HTMLElement} input - the input element.
	 * @param {boolean} skipUpdate - enable status update.
	 */
	form.changeLog.remove = function(input, skipUpdate) {
		const index = this.items.indexOf(input);
		if (index < 0) return;
		
		// if input is found in items, then remove and update countSer
		this.items.splice(index, 1);
		if (!skipUpdate) this.updateStatus();
	};

	/**
	 * Check if there are any changes for an input.
	 * @param {HTMLElement} input - the input element.
	 * @returns true if field was changed.
	 */
	form.changeLog.checkChanges = function(input) {
		// Cleanse and get the current value.
		let currentValue = input.value.replace(/</gi, '&lt;').replace(/>/gi, '&gt;');

		// For checkbox, use the checked value.
		if (input.type == 'checkbox') currentValue = input.checked ? input.checked.toString() : 'false';

		const changed = currentValue != input.dataset.originalValue;

		// When checking input changes with siblings, remove sibling changes first.
		// Sibling changes occur in radio options and checkboxes.
		if (input.siblingOptions) {
			for (let i = 0; i < input.siblingOptions.length; i++) {
				this.remove(input.siblingOptions[i], changed);
			}
		}

		// If the current value is different from the original value saved, add the field to the change log.
		if (changed) this.add(input);
		else this.remove(input);

		// console.log(input, changed, form.changeLog.items)
		return changed;
	};

	// Setup back button to trigger LeaveDiscardChanges dialog.
	SunergoPlatform.actionButtons.addAction(function(action, button) {
		if (action != 'form-leave-discard-changes-dialog') return false;

		if (form.changeLog.items.length < 1) {
			button.parentElement.submit();
			return true;
		}

		const dialogButtonPrimary = form.changeLog.dialogDiscardChanges.$el.querySelector('[data-button-primary]');

		// Reset the href since it might be modified by a link click.
		if (dialogButtonPrimary) dialogButtonPrimary.setAttribute('href', dialogButtonPrimary.dataset.hrefOriginal);

		form.changeLog.dialogDiscardChanges.show();
		return true;
	});

	// Get and loop through all the inputs in form.
	const inputs = form.element.querySelectorAll(inputElements);
	inputs.forEach(function(input) {
		if (input.readOnly) return;

		// Don't validate tom-select input fields.
		if (input.getAttribute('role') === 'combobox') return;

		// Get the field container for the input.
		const field = input.closest('.sp-field');
		
		if (field) {
			if (field.dataset.disableTrackChanges) return;

			// Save the Original Value from the field.
			// Original Value can be stored in the dataset or in an HTMLElement.
			const ovContainer = field.querySelector('[data-original-value]');
			input.dataset.originalValue =
				field.dataset.originalValue ||
				(ovContainer && (ovContainer.dataset.originalValue || ovContainer.textContent));
		}

		const eventTriggered = function() {
			form.changeLog.checkChanges(input);
		};

		switch (input.type) {
			case 'checkbox': {
				if (!input.dataset.originalValue) break;

				// For checkbox type, handle Original Value as an array.
				const checkedValues = JSON.parse(input.dataset.originalValue);

				// Save the truthiness of the value if there is more than 1 checked value.
				if (checkedValues.length > 0)
					input.dataset.originalValue = checkedValues.includes(input.value);
				else input.dataset.originalValue = input.dataset.originalValue == 'true';

				input.addEventListener('change', eventTriggered);
				break;
			}

			case 'radio': {
				// For radio type, get and save sibling options.
				// When a radio is selected, siblings need to be checked for changes as well.
				const fieldInputs = Array.from(field.getElementsByTagName('input'));
				const currentIndex = fieldInputs.indexOf(input);
				fieldInputs.splice(currentIndex, 1);
				input.siblingOptions = fieldInputs;

				input.addEventListener('change', eventTriggered);
				break;
			}

			default:
				input.addEventListener('input', eventTriggered);
				break;
		}
	});
};

export default initTrackChanges;